const applications = [
  {
    icon: "ri-building-line",
    title: "Commercial & Facility Cleaning",
    desc: "Enzyme-powered formulations that eliminate grease, biofilm, and industrial residue from large-scale facilities — without harsh solvents or toxic runoff.",
    metric: "99.9% pathogen elimination",
  },
  {
    icon: "ri-tools-line",
    title: "Industrial Equipment Degreasing",
    desc: "Bio-based degreasers engineered for heavy manufacturing environments. Outperform petroleum solvents on CNC machinery, conveyor systems, and production lines.",
    metric: "3× faster breakdown",
  },
  {
    icon: "ri-shield-check-line",
    title: "Surface Sanitization Protocols",
    desc: "Hospital-grade, bio-derived sanitizers safe for food-contact surfaces, cleanrooms, and regulated environments. Fully compliant with EPA and EU Biocidal Products Regulation.",
    metric: "EPA & EU compliant",
  },
  {
    icon: "ri-drop-line",
    title: "High-Performance Bio-Lubricants",
    desc: "Bio-derived lubricants that outperform petroleum counterparts in viscosity stability and thermal resistance — meeting ISO 15380 Type E standards with 40% longer service life.",
    metric: "40% longer service life",
  },
];

const industries = [
  { icon: "ri-stock-line", name: "Fortune 500 Corporations" },
  { icon: "ri-hospital-line", name: "Healthcare & Pharma" },
  { icon: "ri-restaurant-line", name: "Food & Beverage" },
  { icon: "ri-plane-line", name: "Aviation & Aerospace" },
  { icon: "ri-ship-line", name: "Marine & Ports" },
  { icon: "ri-government-line", name: "Government & Defense" },
];

export default function IndustrialBiotechSection() {
  return (
    <section id="industrial-biotech" className="bg-[#f4faf7] py-28 relative overflow-hidden">
      {/* Subtle dot pattern */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: "radial-gradient(circle, #00c87a25 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="mb-6">
            <span className="text-4xl md:text-5xl lg:text-6xl font-black tracking-widest uppercase bg-clip-text text-transparent bg-gradient-to-r from-[#00c896] via-[#00ffcc] to-[#00a882]">
              Gemtek
            </span>
          </div>
          <h2 className="text-6xl md:text-7xl lg:text-8xl font-black text-[#0a2a1f] leading-none tracking-tight">
            Bio-Based
          </h2>
          <div className="text-5xl md:text-6xl lg:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-[#00a882] to-[#00d4a8] mt-3 leading-none">
            Cleaning Solutions
          </div>
          <p className="text-[#0a2a1f]/65 text-xl md:text-2xl leading-relaxed font-light max-w-2xl mx-auto mt-6">
            Gemtek delivers enterprise-grade, bio-derived cleaning and sanitation products trusted by Fortune 500 companies across the world&apos;s most demanding industries.
          </p>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
          {/* Left: Applications */}
          <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-5 auto-rows-fr">
            {applications.map((app) => (
              <div
                key={app.title}
                className="rounded-xl border border-[#0a2a1f]/10 bg-white p-6 hover:border-[#00a882]/40 hover:bg-[#f0fdf9] transition-all duration-300 group"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#00a882]/10 border border-[#00a882]/20 mb-4 group-hover:bg-[#00a882]/15 transition-colors">
                  <i className={`${app.icon} text-[#00a882] text-lg`}></i>
                </div>
                <h3 className="text-[#0a2a1f] font-bold text-base mb-2">{app.title}</h3>
                <p className="text-[#0a2a1f]/80 text-sm leading-relaxed mb-4">{app.desc}</p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#00a882]/10 border border-[#00a882]/20">
                  <i className="ri-check-line text-[#00a882] text-xs"></i>
                  <span className="text-[#00a882] text-xs font-medium">{app.metric}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Right: Visual + info */}
          <div className="lg:col-span-2 flex flex-col gap-6">
            <div className="relative w-full h-72 rounded-2xl overflow-hidden border border-[#0a2a1f]/10">
              <img
                src="https://public.readdy.ai/ai/img_res/1807e5057de5070727f7e24c22d59d4b.jpg"
                alt="Gemtek sustainable agriculture and biotech"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a2a1f]/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-5 left-5">
                <div className="text-white font-bold text-base">Gemtek</div>
              </div>
            </div>

            {/* Industries served */}
            <div className="rounded-xl border border-[#0a2a1f]/10 bg-white p-6">
              <div className="flex items-center justify-center gap-2 mb-6">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#00a882]/10 border border-[#00a882]/20">
                  <i className="ri-global-line text-[#00a882] text-base"></i>
                </div>
                <div className="text-[#0a2a1f] font-bold text-lg">Industries We Serve</div>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {industries.map((ind) => (
                  <div key={ind.name} className="flex items-center justify-center gap-2 bg-[#f4faf7] rounded-lg px-3 py-3 border border-[#0a2a1f]/8">
                    <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                      <i className={`${ind.icon} text-[#00a882] text-base`}></i>
                    </div>
                    <span className="text-[#0a2a1f]/85 text-sm font-semibold leading-tight">{ind.name}</span>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-3">
                {[
                  { label: "Enterprise Clients", value: "200+" },
                  { label: "Fortune 500", value: "40+" },
                  { label: "Countries", value: "18" },
                  { label: "CO₂ Reduction", value: "73%" },
                ].map((s) => (
                  <div key={s.label} className="bg-[#f4faf7] rounded-lg p-3 border border-[#0a2a1f]/8 text-center">
                    <div className="text-[#00a882] font-black text-xl">{s.value}</div>
                    <div className="text-[#0a2a1f]/65 text-xs mt-1 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}
